import React, { useEffect, useState } from 'react';
import { IRootDispatch, IRootState } from 'stores/rematch/root-store';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import _ from 'lodash';
import { ServiceAgreementDetails } from '../ServiceAgreementDetails';
import SpinningLoader from 'common-components/loading/SpinningLoader';
import { IServiceAgreement } from 'interfaces/customer-interfaces';
import { Anchor, Box, Modal, Paper, Stack, Table, Group, ThemeIcon, Center, Text, Divider } from '@good/ui/core';
import { FileDownload, FileDraft, FilePlus } from '@good/icons';
import { SignedStatusBadge } from './signed-status-badge';
import { ServiceAgreementHistorySubheader } from './service-agreement-history-modal-subheader';
import { ServiceAgreementHistoryModalStep } from './types';
import { notifications } from '@good/ui/notifications';

interface IServiceAgreementHistoryListModalProps {
  serviceAgreementId: string;
  isOpen: boolean;
  openViewModal: (service?: unknown) => void;
  onClose: () => void;
  onClickActionModal: (item: IServiceAgreement, action: string, value: unknown | null) => void;
}

export const ServiceAgreementHistoryListModal = ({
  serviceAgreementId,
  isOpen,
  openViewModal,
  onClose,
  onClickActionModal,
}: IServiceAgreementHistoryListModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<ServiceAgreementHistoryModalStep>('listing');
  const { customersStore } = useDispatch<IRootDispatch>();
  const customerServiceAgreementHistories = useSelector(
    (state: IRootState) => state.customersStore.customerServiceAgreementHistories,
  );
  const selectedCustomerServiceAgreementHistory = useSelector(
    (state: IRootState) => state.customersStore.selectedCustomerServiceAgreementHistory,
  );
  const companyDataLite = useSelector((state: IRootState) => state.companyStore.companyDataLite);
  const doGetServiceAgreementHistories = customersStore.doGetServiceAgreementHistories;
  const doGetServiceAgreementHistoryDetail = customersStore.doGetServiceAgreementHistoryDetail;

  const hasSignedUrl =
    selectedCustomerServiceAgreementHistory?.signedUrl !== null &&
    selectedCustomerServiceAgreementHistory?.signedUrl !== undefined;

  const closeModal = () => {
    if (isLoading) {
      return;
    }

    setIsLoading(false);
    onClose();
  };

  const onClickViewServiceAgreementHistory = async (userServiceAgreementHistoryId: string) => {
    await doGetServiceAgreementHistoryDetail({
      serviceAgreementId,
      userServiceAgreementHistoryId,
    });

    setStep('detail');
  };

  useEffect(() => {
    (async () => {
      await doGetServiceAgreementHistories({
        serviceAgreementId,
      });
    })();
  }, [doGetServiceAgreementHistories, serviceAgreementId]);

  const onDownloadPdf = () => {
    if (!selectedCustomerServiceAgreementHistory) {
      notifications.show({
        title: 'Error',
        message: 'Unable to download PDF. Please try again later.',
        color: 'red',
      });
      return;
    }

    const historyId = selectedCustomerServiceAgreementHistory.userServiceAgreementHistoryId;
    const signedUrl = selectedCustomerServiceAgreementHistory.signedUrl;

    if (signedUrl) {
      window.open(signedUrl, '_blank');
      return;
    }

    const url = `/pdf?type=service-agreement&agreement=${serviceAgreementId}&historyId=${historyId}`;
    window.open(url, '_blank');
  };

  return (
    <Modal
      opened={isOpen}
      onClose={closeModal}
      size={1200}
      title='Service Agreement Version History'
      scrollable
      subHeader={
        <ServiceAgreementHistorySubheader
          step={step}
          serviceAgreementHistory={selectedCustomerServiceAgreementHistory}
        />
      }
      backProps={
        step === 'detail'
          ? {
              onClick: () => {
                setStep('listing');
              },
            }
          : undefined
      }
      confirmProps={
        step === 'detail'
          ? {
              label: hasSignedUrl ? 'Download signed PDF' : 'Download unsigned PDF',
              onClick: onDownloadPdf,
              leftSection: <FileDownload />,
            }
          : undefined
      }
      cancelProps={{
        onClick: closeModal,
      }}
    >
      {step === 'listing' && (
        <Stack gap='lg'>
          <Paper withBorder>
            <Table verticalSpacing='lg'>
              <Table.Thead>
                <Table.Tr c='dimmed' tt='uppercase'>
                  <Table.Th py='xs' />
                  <Table.Th py='xs'>Date</Table.Th>
                  <Table.Th py='xs'>Changes Made</Table.Th>
                  <Table.Th py='xs'>Signed?</Table.Th>
                  <Table.Th py='xs' />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {customerServiceAgreementHistories.map((history) => {
                  return (
                    <Table.Tr key={history.userServiceAgreementHistoryId}>
                      <Center>
                        <Table.Td>
                          <ThemeIcon variant='default' size='lg'>
                            {history.description === 'New Service Agreement created' && <FilePlus />}
                            {history.description !== 'New Service Agreement created' && <FileDraft />}
                          </ThemeIcon>
                        </Table.Td>
                      </Center>
                      <Table.Td>
                        <Box>
                          <Text fz='inherit' fw={600} lh={1.25}>
                            {moment(history.createdOn).format('DD/MM/YYYY')}
                          </Text>
                          <Text fz='inherit' c='dimmed' lh={1.25}>
                            by {history.createdByFirstName} {history.createdByLastName}
                          </Text>
                        </Box>
                      </Table.Td>
                      <Table.Td fw={600}>
                        <Box>
                          <Text fz='inherit'>{history.description}</Text>
                          {history.signedBy && (
                            <Text fz='inherit' c='dimmed'>
                              Signed by {history.signedBy} on {moment(history.signedDate).format('DD/MM/YYYY')}
                            </Text>
                          )}
                          {!history.signedBy && history.signedStatus === 'SIGNED' && (
                            <Text fz='inherit' c='dimmed'>
                              Signed manually on {moment(history.signedDate).format('DD/MM/YYYY')}
                            </Text>
                          )}
                        </Box>
                      </Table.Td>
                      <Table.Td>
                        <SignedStatusBadge signed={history.signedStatus === 'SIGNED'} size='md' />
                      </Table.Td>
                      <Table.Td>
                        <Anchor
                          fz='inherit'
                          fw={600}
                          onClick={() => onClickViewServiceAgreementHistory(history.userServiceAgreementHistoryId)}
                        >
                          View
                        </Anchor>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Paper>
        </Stack>
      )}
      {step === 'detail' && (
        <Box>
          <Box bg='gray.0' m='calc(-1 * var(--mb-padding))' p='var(--mb-padding)'>
            <Paper withBorder shadow='xl'>
              {selectedCustomerServiceAgreementHistory ? (
                <Stack gap={0}>
                  <Group px='lg' py='md'>
                    <SignedStatusBadge
                      signed={selectedCustomerServiceAgreementHistory.signedStatus === 'SIGNED'}
                      size='lg'
                    />
                  </Group>
                  <Divider />
                  <Box p='xl'>
                    <ServiceAgreementDetails
                      item={selectedCustomerServiceAgreementHistory}
                      openViewModal={openViewModal}
                      isHistory
                      hasEditPermission={false}
                      onClickActionModal={onClickActionModal}
                      companyData={companyDataLite}
                      customerId={selectedCustomerServiceAgreementHistory.userId as string}
                      className=''
                    />
                  </Box>
                </Stack>
              ) : (
                <Center>
                  <SpinningLoader size={50} message={'Editing...'} />
                </Center>
              )}
            </Paper>
          </Box>
        </Box>
      )}
    </Modal>
  );
};
